<script>
import { frontendURL } from '../../../helper/URLHelper';
import SecondaryNavItem from './SecondaryNavItem.vue';
import AccountContext from './AccountContext.vue';
import { mapGetters } from 'vuex';
import { FEATURE_FLAGS } from '../../../featureFlags';
import Thumbnail from '../../widgets/Thumbnail.vue';
import NotificationBell from './NotificationBell.vue';
import OptionsMenu from './OptionsMenu.vue';
import {
  getUserPermissions,
  hasPermissions,
} from '../../../helper/permissionsHelper';
import { routesWithPermissions } from '../../../routes';
import { getBrandIconName } from '../../../helper/inbox';

export default {
  components: {
    AccountContext,
    SecondaryNavItem,
    Thumbnail,
    NotificationBell,
    OptionsMenu
  },
  props: {
    accountId: {
      type: Number,
      default: 0,
    },
    labels: {
      type: Array,
      default: () => [],
    },
    inboxes: {
      type: Array,
      default: () => [],
    },
    teams: {
      type: Array,
      default: () => [],
    },
    customViews: {
      type: Array,
      default: () => [],
    },
    menuConfig: {
      type: Object,
      default: () => {},
    },
    currentUser: {
      type: Object,
      default: () => {},
    },
    isOnHichatCloud: {
      type: Boolean,
      default: false,
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['addLabel', 'toggleAccounts', 'closeSidebar', 'openKeyShortcutModal'],
  data() {
    return {
      touchStartX: null,
      touchStartY: null,
      touchMoved: false,
      showOptionsMenu: false,
      optionsMenuStyles: {},
    };
  },
  mounted() {
    // Recompute position on viewport changes when menu is open
    this._repositionHandler = () => {
      if (this.showOptionsMenu) this.computeOptionsMenuPosition();
    };
    window.addEventListener('resize', this._repositionHandler, { passive: true });
    window.addEventListener('scroll', this._repositionHandler, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this._repositionHandler);
    window.removeEventListener('scroll', this._repositionHandler);
  },
  computed: {
    ...mapGetters({
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      currentUserAvailability: 'getCurrentUserAvailability',
      currentAccount: 'getCurrentAccount',
      userAccounts: 'getUserAccounts',
    }),
    statusOfAgent(){
      return this.currentUserAvailability || 'offline';
    },
    contactCustomViews() {
      return this.customViews.filter(view => view.filter_type === 'contact');
    },
    orderCustomViews() {
      return this.customViews.filter(
        view => view.filter_type === 'order_entity'
      );
    },
    baseMenuItems() {
      const filteredByPermissions = this.menuConfig.menuItems.filter(
        menuItem => {
          const userPermissions = getUserPermissions(
            this.currentUser,
            this.accountId
          );
          return hasPermissions(
            routesWithPermissions[menuItem.toStateName],
            userPermissions
          );
        }
      );
      return filteredByPermissions.filter(item => {
        if (item.showOnlyOnCloud) {
          return this.isOnHichatCloud;
        }
        if (item.featureFlag) {
          return this.isFeatureEnabledonAccount(this.accountId, item.featureFlag);
        }
        return true;
      });
    },
    contactMenuItems() {
      const items = [...this.baseMenuItems];

      if (this.contactCustomViews.length) {
        items.push(this.contactSegmentsSection);
      }

      if (this.labels.length) {
        items.push(this.contactLabelSection);
      }

      if (this.inboxes.length) {
        items.push(this.contactChannelSection);
      }

      return items;
    },
    accessibleMenuItems() {
      if (this.menuConfig.parentNav === 'contacts') {
        return this.contactMenuItems;
      }

      return this.baseMenuItems;
    },
    inboxSection() {
      return {
        icon: 'folder',
        label: 'INBOXES',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.INBOX_MANAGEMENT),
        newLinkTag: 'NEW_INBOX',
        key: 'inbox',
        toState: frontendURL(`accounts/${this.accountId}/settings/inboxes/new`),
        toStateName: 'settings_inbox_new',
        newLinkRouteName: 'settings_inbox_new',
        children: this.inboxes
          .map(inbox => ({
            id: inbox.id,
            label: inbox.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/inbox/${inbox.id}`
            ),
            type: inbox.channel_type,
            phoneNumber: inbox.phone_number,
            reauthorizationRequired: inbox.reauthorization_required,
            brandIcon: getBrandIconName(inbox.channel_type, inbox),
          }))
          .sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
          ),
      };
    },
    labelSection() {
      return {
        label: 'LABELS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.LABELS),
        newLinkTag: 'NEW_LABEL',
        key: 'label',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        dataTestid: 'sidebar-new-label-button',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/label/${label.title}`
          ),
        })),
      };
    },
    contactLabelSection() {
      return {
        label: 'TAGGED_WITH',
        hasSubMenu: true,
        key: 'labels',
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/contacts/labels/${label.title}`
          ),
        })),
      };
    },
    contactChannelSection() {
      return {
        label: 'CHANNELS',
        hasSubMenu: true,
        key: 'channels',
        children: this.inboxes
          .map(inbox => ({
            id: inbox.id,
            label: inbox.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/contacts/inbox/${inbox.id}`
            ),
            type: inbox.channel_type,
            phoneNumber: inbox.phone_number,
            reauthorizationRequired: inbox.reauthorization_required,
            brandIcon: getBrandIconName(inbox.channel_type, inbox),
          }))
          .sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
          ),
      };
    },
    teamSection() {
      return {
        label: 'TEAMS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_TEAM',
        key: 'team',
        toState: frontendURL(`accounts/${this.accountId}/settings/teams/new`),
        toStateName: 'settings_teams_new',
        newLinkRouteName: 'settings_teams_new',
        children: this.teams.map(team => ({
          id: team.id,
          label: team.name,
          truncateLabel: true,
          toState: frontendURL(`accounts/${this.accountId}/team/${team.id}`),
          icon: 'user-plus',
        })),
      };
    },
    foldersSection() {
      return {
        label: 'CUSTOM_VIEWS_FOLDER',
        hasSubMenu: true,
        key: 'custom_view',
        children: this.customViews
          .filter(view => view.filter_type === 'conversation')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/custom_view/${view.id}`
            ),
          })),
      };
    },
    contactSegmentsSection() {
      return {
        label: 'CUSTOM_VIEWS_SEGMENTS',
        hasSubMenu: true,
        key: 'segments',
        children: this.customViews
          .filter(view => view.filter_type === 'contact')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/contacts/segments/${view.id}`
            ),
          })),
      };
    },
    orderSegmentsSection() {
      return {
        label: 'CUSTOM_VIEWS_SEGMENTS',
        hasSubMenu: true,
        key: 'segments',
        children: this.customViews
          .filter(view => view.filter_type === 'order_entity')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/orders/segments/${view.id}`
            ),
          })),
      };
    },
    additionalSecondaryMenuItems() {
      let conversationMenuItems = [];
      let contactMenuItems = [this.contactLabelSection];
      let orderMenuItems = [];
      if (this.customViews.length) {
        conversationMenuItems = [this.foldersSection];
      }
      conversationMenuItems = [
        ...conversationMenuItems,
        this.inboxSection,
        ...(this.teams.length ? [this.teamSection] : []),
        this.labelSection
      ];
      if (this.contactCustomViews.length) {
        contactMenuItems = [this.contactSegmentsSection, ...contactMenuItems];
      }
      if (this.orderCustomViews.length) {
        orderMenuItems = [this.orderSegmentsSection, ...orderMenuItems];
      }
      return {
        conversations: conversationMenuItems,
        contacts: contactMenuItems,
        orders: orderMenuItems,
      };
    },
    inboxMenuItems() {
      let conversationMenuItems = [];
      if (this.customViews.length) {
        conversationMenuItems = [this.foldersSection];
      }
      conversationMenuItems = [
        ...conversationMenuItems,
        this.inboxSection,
        ...(this.teams.length ? [this.teamSection] : [])
      ];
      return conversationMenuItems;
    },
    labelMenuItems() {
      return [this.labelSection];
    },
    showShowCurrentAccountContext() {
      return this.userAccounts.length > 1 && this.currentAccount?.name;
    },
  },
  methods: {
    showAddLabelPopup() {
      this.$emit('addLabel');
    },
    toggleOptions() {
      this.showOptionsMenu = !this.showOptionsMenu;
      if (this.showOptionsMenu) {
        this.$nextTick(() => {
          this.computeOptionsMenuPosition();
        });
      }
    },
    computeOptionsMenuPosition() {
      try {
        const primaryRef = this.$refs?.optionsBtnEl || this.$refs?.avatarEl;
        if (!primaryRef) return;
        // Support refs on child/DOM element by accessing $el if present
        const anchorEl = primaryRef.$el ? primaryRef.$el : primaryRef;
        const rect = anchorEl.getBoundingClientRect();
        const menuWidth = 256; // w-64
        const gap = 8; // 8px below avatar
        // Place menu to the LEFT of the button: align menu's right edge to button's left edge
        let left = rect.left - menuWidth;
        // ensure within viewport with 8px padding
        const minLeft = 8;
        const maxLeft = window.innerWidth - menuWidth - 8;
        left = Math.max(minLeft, Math.min(left, maxLeft));
        const top = rect.bottom + gap;
        this.optionsMenuStyles = {
          position: 'fixed',
          top: `${top}px`,
          left: `${left}px`,
          right: 'auto',
          bottom: 'auto',
          zIndex: 1000,
        };
      } catch (e) {
        // noop
      }
    },
    toggleAccountModal() {
      this.$emit('toggleAccounts');
    },
    toggleSupportChatWindow() {
      if (window && window.$hichat) {
        window.$hichat.toggle();
      }
    },
    onItemNavigate() {
      // Auto-close secondary sidebar on mobile after a navigation
      if (this.isMobileView) {
        this.$emit('closeSidebar');
      }
    },
    showNewLink(featureFlag) {
      return this.isFeatureEnabledonAccount(this.accountId, featureFlag);
    },
     // --- swipe to close (mobile) ---
    onTouchStart(e) {
      if (!this.isMobileView) return;
      this.touchStartX = e.touches?.[0]?.clientX || 0;
      this.touchStartY = e.touches?.[0]?.clientY || 0;
      this.touchMoved = false;
    },
    onTouchMove(e) {
      if (!this.isMobileView || this.touchStartX == null) return;
      const x = e.touches?.[0]?.clientX || 0;
      const y = e.touches?.[0]?.clientY || 0;
      const dx = x - this.touchStartX;
      const dy = y - this.touchStartY;
      this.touchMoved = Math.abs(dx) > 10 && Math.abs(dy) < 30; // ưu tiên ngang
    },
    onTouchEnd(e) {
      if (!this.isMobileView || this.touchStartX == null) return;
      const endX = e.changedTouches?.[0]?.clientX || 0;
      const dx = endX - this.touchStartX;
      // vuốt sang trái ≥ 60px -> đóng
      if (this.touchMoved && dx <= -60) {
        this.$emit('closeSidebar');
      }
      this.touchStartX = null;
      this.touchStartY = null;
      this.touchMoved = false;
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col w-full xl:w-[229px] h-full px-4 pb-8 overflow-auto text-sm bg-gradient-to-b from-slate-50/80 to-white/90 dark:from-slate-900/80 dark:to-slate-800/90 backdrop-blur-sm shadow-lg"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
  <div class="flex items-center gap-3 py-2 mb-2 rounded-lg shadow-[0_4px_6px_-4px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_6px_-4px_rgba(0,0,0,0.3)]">
    <Thumbnail
      v-tooltip.right="$t(`SIDEBAR.PROFILE_SETTINGS`)"
      :src="currentUser.avatar_url"
      :username="currentUser.name"
      :status="currentUserAvailability || 'offline'"
      should-show-status-always
      size="32px"
      class="cursor-default"
    />
    <div class="min-w-0">
      <p class="mb-0 text-n-slate-12 dark:text-n-slate-12 font-semibold leading-tight break-words whitespace-normal">
        {{ currentUser.name }}
      </p>
      <p class="mb-0 text-n-slate-10 text-xs leading-tight break-words whitespace-normal">
        {{ currentAccount?.name || '' }}
      </p>
    </div>
    <div class="ml-auto flex items-center gap-1">
      <button
        @click="toggleOptions"
        ref="optionsBtnEl"
        class="p-1 rounded-md hover:bg-n-gray-2 dark:hover:bg-n-black/40 transition-colors"
        v-tooltip.right="$t('PROFILE_SETTINGS.LINK')"
        aria-label="More options"
      >
        <lucide-icon name="ellipsis-vertical" class="size-5" />
      </button>
      <NotificationBell v-if="isMobileView" :to="{ name: 'notifications_index' }" />
    </div>
  </div> 
  <!-- Options menu teleported to body to avoid clipping -->
  <teleport to="body">
    <OptionsMenu
      :show="showOptionsMenu"
      :style="optionsMenuStyles"
      @toggle-accounts="toggleAccountModal"
      @show-support-chat-window="toggleSupportChatWindow"
      @open-key-shortcut-modal="$emit('openKeyShortcutModal')"
      @close="toggleOptions"
    />
  </teleport>
  <AccountContext />
    
    <!-- Inboxes Section - Only show for Conversations -->
    <transition-group
      v-if="menuConfig.parentNav === 'conversations'"
      name="menu-list"
      tag="ul"
      class="py-4 list-none reset-base rounded-xl backdrop-blur-sm shadow-sm mx-1 mb-3"
      >
      <SecondaryNavItem
        v-for="menuItem in accessibleMenuItems"
        :key="menuItem.toState"
        :menu-item="menuItem"
        @navigate="onItemNavigate"
      />
      <SecondaryNavItem
        v-for="menuItem in inboxMenuItems"
        :key="menuItem.key"
        :menu-item="menuItem"
        @add-label="showAddLabelPopup"
        @navigate="onItemNavigate"
      />
    </transition-group>
    
    <!-- Labels Section - Only show for Conversations -->
    <transition-group
      v-if="menuConfig.parentNav === 'conversations'"
      name="menu-list"
      tag="ul"
      class="py-4 list-none reset-base rounded-xl backdrop-blur-sm shadow-sm mx-1 mb-3"
      >
      <SecondaryNavItem
        v-for="menuItem in labelMenuItems"
        :key="menuItem.key"
        :menu-item="menuItem"
        @add-label="showAddLabelPopup"
        @navigate="onItemNavigate"
      />
    </transition-group>

    <!-- Default menu items for other sections (Reports, Settings, etc.) -->
    <transition-group
      v-if="menuConfig.parentNav !== 'conversations'"
      name="menu-list"
      tag="ul"
      class="py-4 list-none reset-base rounded-xl backdrop-blur-sm shadow-sm mx-1 mb-3"
      >
      <SecondaryNavItem
        v-for="menuItem in accessibleMenuItems"
        :key="menuItem.toState"
        :menu-item="menuItem"
        @add-label="showAddLabelPopup"
        @navigate="onItemNavigate"
      />
    </transition-group>
  </div>
</template>
